import { mergeToolBlocks } from '../MessageBlockRenderer';

function buildFallbackToolName(name) {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function parseAssistantContent(msg) {
    const rawBlocks = Array.isArray(msg.content) ? msg.content : [];
    const blocks = [];

    for (const block of rawBlocks) {
        if (block.type === 'thinking') {
            blocks.push({ type: 'thinking', thinking: block.thinking || '' });
        } else if (block.type === 'text') {
            const extras = block.extras;
            if (extras?.block_subtype === 'user_stopped') {
                blocks.push({ type: 'terminal_user_stopped', content: block.text || '', extras });
            } else if (extras?.block_subtype === 'error') {
                blocks.push({ type: 'terminal_error', content: block.text || '', extras });
            } else {
                blocks.push({
                    type: 'text',
                    content: block.text || '',
                    isFinal: block.is_final || false,
                    isPart: block.is_part || false,
                });
            }
        } else if (block.type === 'tool_use') {
            const { tool_content_message, ...cleanInput } = (block.input || {});
            blocks.push({
                type: 'tool_use',
                id: block.id,
                name: block.name,
                input: cleanInput,
                tool_content_message: tool_content_message || buildFallbackToolName(block.name),
            });
        } else if (block.type === 'approval_request') {
            blocks.push(block);
        }
    }

    if (msg.tool_calls?.length > 0) {
        for (const tc of msg.tool_calls) {
            if (blocks.some(b => b.type === 'tool_use' && b.id === tc.id)) continue;
            const { tool_content_message: _tcm, ...cleanArgs } = (tc.args || {});
            blocks.push({
                type: 'tool_use',
                id: tc.id,
                name: tc.name,
                input: cleanArgs,
                tool_content_message: tc.tool_content_message || buildFallbackToolName(tc.name),
            });
        }
    }

    if (msg.attachments?.length > 0) {
        blocks.push({ type: 'attachments', files: msg.attachments });
    }

    return blocks;
}

function parseToolContent(msg) {
    const block = {
        type: 'tool_result',
        tool_use_id: msg.tool_call_id,
        name: msg.name,
        content: msg.content,
        status: msg.status || 'success',
    };
    if (msg.artifact) block.artifact = msg.artifact;
    return [block];
}

export function convertHistory(messages) {
    const blocks = [];

    for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        const displayType = msg.display_type;
        const messageType = msg.role === 'assistant' ? msg.message_type : undefined;

        if (msg.role === 'user') {
            let textContent = '';
            const attachments = [];
            if (typeof msg.content === 'string') {
                textContent = msg.content;
            } else if (Array.isArray(msg.content)) {
                for (const block of msg.content) {
                    if (block.type === 'text' && block.text) {
                        textContent += (textContent ? '\n' : '') + block.text;
                    } else if (block.type === 'attachment') {
                        attachments.push(block);
                    }
                }
            }
            blocks.push({
                type: 'user',
                content: textContent,
                attachments: attachments.length > 0 ? attachments : undefined,
            });
            continue;
        }

        if (displayType === 'content') {
            const parsed = msg.role === 'assistant'
                ? parseAssistantContent(msg)
                : parseToolContent(msg);
            if (messageType === 'chat' && parsed.length > 0) {
                parsed[0].message_type = 'chat';
            }
            blocks.push(...parsed);
            continue;
        }

        if (displayType === 'group_start') {
            const groupMarker = { type: 'group_start', summary: msg.summary || '' };
            if (messageType === 'chat') groupMarker.message_type = 'chat';
            blocks.push(groupMarker);
            blocks.push(...parseAssistantContent(msg));
            if (msg.group_closed) {
                blocks.push({ type: 'group_end', summary: msg.summary || '' });
            }
            continue;
        }

        if (displayType === 'group_item') {
            const parsed = msg.role === 'assistant'
                ? parseAssistantContent(msg)
                : parseToolContent(msg);
            blocks.push(...parsed);
            continue;
        }

        if (displayType === 'group_end') {
            const parsed = msg.role === 'assistant'
                ? parseAssistantContent(msg)
                : parseToolContent(msg);
            blocks.push(...parsed);
            blocks.push({ type: 'group_end', summary: msg.summary || '' });
            continue;
        }

        // Fallback: no display_type (backward compat)
        const parsed = msg.role === 'assistant'
            ? parseAssistantContent(msg)
            : parseToolContent(msg);
        if (msg.role === 'assistant') {
            let j = i + 1;
            while (j < messages.length && messages[j].role === 'tool' && !messages[j].display_type) {
                parsed.push(...parseToolContent(messages[j]));
                j++;
            }
            i = j - 1;
        }
        blocks.push(...parsed);
    }

    return mergeToolBlocks(blocks);
}
